'use-client';

import {
  BottomNavBar,
  TBottomNavBarProps,
} from '@/lib/foundations/layouts/bottom-nav-bar';
import { Panel } from '@/lib/foundations/layouts/panel';
import { Drawer } from '@/lib/foundations/layouts/drawer';
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { Button } from '@/lib/foundations/controls/button';
import {
  TUseInteractivePlayer,
  useInteractivePlayer,
} from '@/api/hooks/use-interactive-player/use-interactive-player';
import { Genre } from '@/api/hooks/use-interactive-player/@enums/genre.enum';
import { Instrument } from '@/api/hooks/use-interactive-player/@enums/instrument.enum';
import { Tempo } from '@/api/hooks/use-interactive-player/@enums/tempo.enum';
import { Density } from '@/api/hooks/use-interactive-player/@enums/density.enum';
import { Soundscape } from '@/api/hooks/use-interactive-player/@enums/soundscape.enum';
import classNames from 'classnames';
import { Toolbar } from '@/lib/foundations/layouts/toolbar';
import { UrlAudioPlayer } from '../url-audio-player';

import { RxHamburgerMenu } from 'react-icons/rx';

import {
  InteractivePlayerBackground,
  TInteractivePlayerBackgroundRef,
} from '../interactive-player-background';
import { ParameterSelectButton } from '@/components/foundations/parameter-select-button';
import { InfinitySlidingMenu } from '../infinity-sliding-menu';
import { TxmyIcon } from '@/components/foundations/txmy-icon';

/* ================================== TYPES ================================= */

type TMusicParameters =
  | 'Instrument'
  | 'Genre'
  | 'Tempo'
  | 'Density'
  | 'Soundscape';

export type TInteractivePlayerParametersMenuProps = {
  shareCode?: string;
};

/* ========================================================================== */
/*                                  COMPONENT                                 */
/* ========================================================================== */
export function InteractivePlayerParametersMenu(
  props: TInteractivePlayerParametersMenuProps,
) {
  const { shareCode } = props;

  const [activeParameter, setActiveParameter] = useState<
    TMusicParameters | undefined
  >(!shareCode ? 'Genre' : undefined);
  const [originalState, setOriginalState] = useState<TUseInteractivePlayer>();
  const [isSlidingMenuOpen, setIsSlidingMenuOpen] = useState(false);

  const state = useInteractivePlayer();
  const backgroundRef = useRef<TInteractivePlayerBackgroundRef>(null);

  const handleParameterButtonPress = (parameter: TMusicParameters) => {
    setActiveParameter(parameter);
    setOriginalState({ ...state });
  };

  const handlePressGenerateButton = async () => {
    await state.session.fetch();
  };

  const handleApplyEffects = () => {
    if (activeParameter === 'Genre') {
      // Reset all other parameters to unknown for Ambient genre
      state.instrument.set(undefined);
      state.tempo.set(undefined);
      state.density.set(undefined);
      state.soundscape.set(undefined);
    }
    setActiveParameter(undefined);
  };

  const handleCancel = () => {
    if (originalState) {
      Object.keys(originalState).forEach((key) => {
        const stateKey = key as keyof TUseInteractivePlayer;
        if (stateKey !== 'session' && state[stateKey]) {
          state[stateKey].set(originalState[stateKey].get);
        }
      });
    }

    setActiveParameter(undefined);
  };

  const handleOpenSlidingMenu = () => {
    setIsSlidingMenuOpen(true);
  };

  const handleSlidingMenuClose = (isOpen: boolean) => {
    setIsSlidingMenuOpen(isOpen);
  };

  useEffect(() => {
    if (shareCode) {
      void state.session.fetchByShareCode(shareCode);
    }
  }, [shareCode]);

  const [defaultContainer, setDefaultContainer] = useState<HTMLElement>();

  const isDrawerOpen = activeParameter !== undefined;

  useEffect(() => {
    if (!defaultContainer) {
      const mainElement = window.document.getElementById(
        'interactive-player-parameters-menu',
      );
      setDefaultContainer(mainElement || undefined);
    }
  }, []);

  const paramControlsWrapperClasses = classNames(
    'flex',
    'flex-wrap',
    'gap-[20px]', // Consistent gap between items
    'justify-center',
    'items-center',
    'w-full',
    'p-4',
  );

  /* ========================= AVAILABLE PARAMETERS ========================= */

  const genreActiveParams = Object.values(Genre);
  const tempoActiveParams = Object.values(Tempo);
  const densityActiveParams = Object.values(Density);

  let instrumentActiveParams = Object.values(Instrument);
  let soundscapeActiveParams = Object.values(Soundscape);

  /**
   * Downbeat doesn't support all soundscapes.
   */
  if (state.genre.get === Genre.Downbeat) {
    soundscapeActiveParams = Object.values(Soundscape).filter(
      (soundscape) =>
        soundscape !== Soundscape.JapaneseGarden &&
        soundscape !== Soundscape.Thunderstorm,
    );
    instrumentActiveParams = Object.values(Instrument).filter(
      (instrument) => instrument !== Instrument.None,
    );
  }

  /**
   * Ambient doesn't support instruments.
   */
  if (state.genre.get === Genre.Ambient) {
    instrumentActiveParams = [];
  }

  /* =========================== PARAMETER LABELS =========================== */

  const genreVisibleLabelMap = {
    [Genre.Ambient]: 'Ambient',
    [Genre.Downbeat]: 'Downbeat',
  };

  const densityVisibleLabelMap = {
    [Density.Dense]: 'Dense',
    [Density.Sparse]: 'Sparse',
    [Density.Standard]: 'Standard',
  };

  const soundscapeVisibleLabelMap = {
    [Soundscape.None]: 'None',
    [Soundscape.JapaneseGarden]: 'Japanese Garden',
    [Soundscape.Thunderstorm]: 'Thunderstorm',
    [Soundscape.Rain]: 'Rain',
    [Soundscape.Sea]: 'Sea',
    [Soundscape.Fire]: 'Fire',
  };

  const tempoVisibleLabelMap = {
    [Tempo.SlowDown]: 'Slow Down',
    [Tempo.Normal]: 'Normal',
    [Tempo.SpeedUp]: 'Speed Up',
  };

  const instrumentVisibleLabelMap = {
    [Instrument.None]: 'None',
    [Instrument.DrumAC]: 'Acoustic Drum',
    [Instrument.DrumElec]: 'Electric Drum',
    [Instrument.DrumLofiA]: 'Lo-Fi Drum A',
    [Instrument.DrumLofiB]: 'Lo-Fi Drum B',
  };

  const handleSetParameter = <T extends keyof TUseInteractivePlayer>(
    property: T,
    value: NonNullable<TUseInteractivePlayer[T]['get']>,
  ) => {
    if ('set' in state[property]) {
      (state[property] as { set: (value: any) => void }).set(value);
    } else {
      console.error(`Property ${property} does not have a 'set' method`);
    }
  };

  const renderButton = <T extends keyof TUseInteractivePlayer>(
    property: T,
    value: NonNullable<TUseInteractivePlayer[T]['get']>,
  ) => {
    const label: string =
      property === 'genre'
        ? genreVisibleLabelMap[value as Genre]
        : property === 'tempo'
          ? tempoVisibleLabelMap[value as Tempo]
          : property === 'density'
            ? densityVisibleLabelMap[value as Density]
            : property === 'soundscape'
              ? soundscapeVisibleLabelMap[value as Soundscape]
              : instrumentVisibleLabelMap[value as Instrument];

    const getIcon = (property: string, value: any) => {
      switch (property) {
        case 'genre':
          switch (value) {
            case Genre.Ambient:
              return null;
            case Genre.Downbeat:
              return null;
            default:
              return null;
          }
        case 'tempo':
          switch (value) {
            case Tempo.SlowDown:
              return <TxmyIcon type="tempo-slow" />;
            case Tempo.Normal:
              return <TxmyIcon type="tempo-normal" />;
            case Tempo.SpeedUp:
              return <TxmyIcon type="tempo-fast" />;
            default:
              return null;
          }
        case 'density':
          switch (value) {
            case Density.Sparse:
              return <TxmyIcon type="density-sparse" />;
            case Density.Standard:
              return <TxmyIcon type="density-normal" />;
            case Density.Dense:
              return <TxmyIcon type="density-condensed" />;
            default:
              return null;
          }
        case 'soundscape':
          switch (value) {
            case Soundscape.None:
              return null;
            case Soundscape.JapaneseGarden:
              return <TxmyIcon type="environment-japanese-garden" />;
            case Soundscape.Thunderstorm:
              return <TxmyIcon type="environment-storm" />;
            case Soundscape.Rain:
              return <TxmyIcon type="environment-rain" />;
            case Soundscape.Sea:
              return <TxmyIcon type="environment-sea" />;
            case Soundscape.Fire:
              return <TxmyIcon type="environment-fire" />;
            default:
              return null;
          }
        case 'instrument':
          switch (value) {
            case Instrument.None:
              return null;
            case Instrument.DrumAC:
              return <TxmyIcon type="drums" />;
            case Instrument.DrumElec:
              return <TxmyIcon type="drums-electronic" />;
            case Instrument.DrumLofiA:
              return <TxmyIcon type="drums-lofi_a" />;
            case Instrument.DrumLofiB:
              return <TxmyIcon type="drums-lofi_b" />;
            default:
              return null;
          }
        default:
          return null;
      }
    };

    const icon = getIcon(property, value);

    return (
      <ParameterSelectButton
        icon={icon}
        label={label}
        onPress={() => handleSetParameter(property, value)}
        isSelected={state[property].get === value}
      />
    );
  };

  /* ========================== PARAMETER CONTROLS ========================== */

  let title = '';
  let description = '';
  let controls: ReactNode = null;

  const descriptionMap: { [param: TMusicParameters]: string } = {
    Genre: '',
    Instrument: 'Types of rhythmic groove for downbeat',
    Tempo: 'How fast or slow the music moves',
    Density: 'How rich or spacious the music feels',
    Soundscape: 'Background sounds from nature or environments',
  };

  switch (activeParameter) {
    case 'Genre':
      title = 'Genre';
      description = descriptionMap['Genre'];
      controls = (
        <div className={paramControlsWrapperClasses}>
          {genreActiveParams.map((genre) => renderButton('genre', genre))}
        </div>
      );
      break;
    case 'Instrument':
      title = 'Instrument';
      description = descriptionMap['Instrument'];
      controls = (
        <div className={paramControlsWrapperClasses}>
          {instrumentActiveParams.map((instrument) =>
            renderButton('instrument', instrument),
          )}
        </div>
      );
      break;
    case 'Tempo':
      title = 'Tempo';
      description = descriptionMap['Tempo'];
      controls = (
        <div className={paramControlsWrapperClasses}>
          {tempoActiveParams.map((tempo) => renderButton('tempo', tempo))}
        </div>
      );
      break;
    case 'Density':
      title = 'Density';
      description = descriptionMap['Density'];
      controls = (
        <div className={paramControlsWrapperClasses}>
          {densityActiveParams.map((density) =>
            renderButton('density', density),
          )}
        </div>
      );
      break;
    case 'Soundscape':
      title = 'Soundscape';
      description = descriptionMap['Soundscape'];
      controls = (
        <div className={paramControlsWrapperClasses}>
          {soundscapeActiveParams.map((soundscape) =>
            renderButton('soundscape', soundscape),
          )}
        </div>
      );
  }

  /* ============================ BOTTOM NAV BAR ============================ */

  const bottomNavBarProps: TBottomNavBarProps = {
    items: [
      {
        label: 'Genre',
        subLabel: state.genre.get
          ? `(${genreVisibleLabelMap[state.genre.get]})`
          : undefined,
        icon: <TxmyIcon type="genre" />,
        action: () => handleParameterButtonPress('Genre'),
        showDividerAfter: true,
      },
      state.genre.get === Genre.Ambient
        ? null
        : {
            label: 'Drums',
            subLabel: state.instrument.get
              ? `(${instrumentVisibleLabelMap[state.instrument.get]})`
              : undefined,
            icon: <TxmyIcon type="drums" />,
            action: () => handleParameterButtonPress('Instrument'),
          },
      {
        label: 'Tempo',
        subLabel: state.tempo.get
          ? `(${tempoVisibleLabelMap[state.tempo.get]})`
          : undefined,
        icon: <TxmyIcon type="tempo-b" />,
        action: () => handleParameterButtonPress('Tempo'),
      },
      {
        label: 'Density',
        subLabel: state.density.get
          ? `(${densityVisibleLabelMap[state.density.get]})`
          : undefined,
        icon: <TxmyIcon type="density-normal" />,
        action: () => handleParameterButtonPress('Density'),
      },
      {
        label: 'Soundscape',
        subLabel: state.soundscape.get
          ? `(${soundscapeVisibleLabelMap[state.soundscape.get]})`
          : undefined,
        icon: <TxmyIcon type="environment" />,
        action: () => handleParameterButtonPress('Soundscape'),
      },
    ].filter(Boolean) as TBottomNavBarProps['items'],
  };

  /* =========================== BACKGROUND ASSETS ========================== */

  const mediaMap: Record<Soundscape, string[]> = {
    [Soundscape.Rain]: [
      '/backgrounds/txmy-ethereal/mp4/rainy-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/rainy-night.mp4',
    ],
    [Soundscape.Thunderstorm]: [
      '/backgrounds/txmy-ethereal/mp4/rainy-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/rainy-night.mp4',
    ],
    [Soundscape.JapaneseGarden]: [
      '/backgrounds/txmy-ethereal/mp4/autumn-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/autumn-night.mp4',
      '/backgrounds/txmy-ethereal/mp4/spring-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/spring-night.mp4',
    ],
    [Soundscape.Sea]: [
      '/backgrounds/txmy-ethereal/mp4/summer-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/summer-night.mp4',
    ],
    [Soundscape.Fire]: [
      '/backgrounds/txmy-ethereal/mp4/winter-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/winter-night.mp4',
    ],
    [Soundscape.None]: [
      '/backgrounds/txmy-ethereal/mp4/autumn-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/autumn-night.mp4',
      '/backgrounds/txmy-ethereal/mp4/spring-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/spring-night.mp4',
      '/backgrounds/txmy-ethereal/mp4/summer-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/summer-night.mp4',
      '/backgrounds/txmy-ethereal/mp4/winter-day.mp4',
      '/backgrounds/txmy-ethereal/mp4/winter-night.mp4',
    ],
  };

  useEffect(() => {
    if (state.session.get?.trackUrl) {
      const soundscape = state.soundscape.get;
      const availableMedia = mediaMap[soundscape] || mediaMap[Soundscape.None];
      const randomMedia =
        availableMedia[Math.floor(Math.random() * availableMedia.length)];

      if (soundscape && soundscape !== Soundscape.None) {
        backgroundRef.current?.selectSpecificBackground(randomMedia);
      } else {
        backgroundRef.current?.selectRandomBackground();
      }
    }
  }, [state.session.get?.trackUrl, state.soundscape.get]);

  const interactiveBackground = (
    <div className="absolute inset-x-0 bottom-0 -top-[100px]">
      <InteractivePlayerBackground
        ref={backgroundRef}
        media={[
          '/backgrounds/txmy-ethereal/mp4/autumn-day.mp4',
          '/backgrounds/txmy-ethereal/mp4/autumn-night.mp4',
          '/backgrounds/txmy-ethereal/mp4/rainy-night.mp4',
          '/backgrounds/txmy-ethereal/mp4/rainy-day.mp4',
          '/backgrounds/txmy-ethereal/mp4/spring-day.mp4',
          '/backgrounds/txmy-ethereal/mp4/spring-night.mp4',
          '/backgrounds/txmy-ethereal/mp4/summer-day.mp4',
          '/backgrounds/txmy-ethereal/mp4/summer-night.mp4',
          '/backgrounds/txmy-ethereal/mp4/winter-day.mp4',
          '/backgrounds/txmy-ethereal/mp4/winter-night.mp4',
        ]}
      />
    </div>
  );

  /* =============================== RENDERING ============================== */

  const generationTriggerWrapperClasses = classNames(
    'relative',
    'inset-x-0',
    'bottom-0',
    'flex',
    'flex-col',
    'items-center',
    'justify-end',
  );

  const generationTriggerClasses = classNames(
    'w-10/12',
    'bg-infinity',
    'rounded-t-lg',
    'text-white',
    'text-center',
    'text-2xl',
    'font-meyer',
    'p-6',
    'border-t-2',
    'border-x-2',
    'border-white',
    'transition-all',
    'duration-300',
    'ease-in-out',
    'bg-opacity-70',
    'border-opacity-70',
    'hover:bg-opacity-90',
    'hover:border-opacity-90',
  );

  const generationTriggerStyles = {
    boxShadow: 'rgba(255, 255, 255, 0.8) 0px 0px 30px',
  };

  const animatedText = 'Generating Awesomeness'
    .split('')
    .map((char, index) => (
      <span key={index}>{char === ' ' ? '\u00A0' : char}</span>
    ));

  const appliedParameters = [
    state.genre.get ? genreVisibleLabelMap[state.genre.get] : undefined,
    state.instrument.get
      ? instrumentVisibleLabelMap[state.instrument.get]
      : undefined,
    state.tempo.get ? tempoVisibleLabelMap[state.tempo.get] : undefined,
    state.density.get ? densityVisibleLabelMap[state.density.get] : undefined,
    state.soundscape.get
      ? soundscapeVisibleLabelMap[state.soundscape.get]
      : undefined,
  ].filter(Boolean);

  const showAudioPlayer =
    !state.session.isLoading && state.session.get?.trackUrl;

  const audioPlayerWrapperClasses = classNames(
    'absolute',
    'inset-0',
    'flex',
    'items-center',
    'justify-center',
    'fade',
    showAudioPlayer && 'show',
  );

  const drawerControlButtonWrapperClasses = classNames(
    'flex',
    'justify-between',
    'gap-2',
    'p-1',
    'absolute',
    'inset-x-2',
    'top-2',
  );

  return (
    <div className="absolute inset-0">
      <div className="absolute inset-0">{interactiveBackground}</div>
      <div className="absolute inset-0">
        <Panel
          contentWidth="stretch"
          useStyledBackground={false}
          useStyledBody={false}
          useStyledFooter={false}
          useStyledHeader={false}
          header={
            <Toolbar
              left={
                <div className="p-4">
                  <Button
                    onPress={handleOpenSlidingMenu}
                    size="lg"
                    variant="ghost"
                    isIconOnly
                  >
                    <RxHamburgerMenu style={{ color: 'white' }} />
                  </Button>
                </div>
              }
            />
          }
          footer={
            <>
              {state.session.isLoading && (
                <div className={generationTriggerWrapperClasses}>
                  <div
                    className={generationTriggerClasses}
                    style={generationTriggerStyles}
                  >
                    <span className="jump-text">{animatedText}</span>
                  </div>
                </div>
              )}
              {!state.session.isLoading && (
                <div className={generationTriggerWrapperClasses}>
                  <button
                    onClick={() => void handlePressGenerateButton()}
                    className={generationTriggerClasses}
                    style={generationTriggerStyles}
                  >
                    {state.session.get?.trackUrl && <>Generate Another Remix</>}
                    {!state.session.get?.trackUrl && <>Generate Remix</>}
                  </button>
                </div>
              )}
              <BottomNavBar {...bottomNavBarProps} />
            </>
          }
          isScrollable
          bodyElementId="interactive-player-parameters-menu"
        >
          <div className={audioPlayerWrapperClasses}>
            <UrlAudioPlayer
              url={state.session.get?.trackUrl}
              trackTitle={
                state.session.get?.shareCode
                  ? `Ethereal [${state.session.get?.shareCode}]`
                  : 'Ethereal'
              }
              subtitle="Txmy Featuring You"
              tags={appliedParameters as string[]}
              albumArt="/backgrounds/txmy-ethereal/jpg/autumn-day.jpg"
              shareCode={state.session.get?.shareCode}
            />
          </div>
        </Panel>
        <Drawer
          isOpen={isDrawerOpen}
          title={title}
          description={description}
          className={{
            title: 'text-3xl font-meyer text-white text-center',
            description: 'text-base font-meyer text-center text-gray-300',
          }}
          onClose={handleCancel}
          container={defaultContainer}
          backgroundColor="infinity" // Dark blue with transparency
          isTransparent
          useBoxShadow
        >
          {isDrawerOpen && (
            <div>
              {controls}
              <div className={drawerControlButtonWrapperClasses}>
                <Button
                  onPress={handleCancel}
                  isIconOnly
                  size="lg"
                  variant="light"
                  radius="lg"
                >
                  <TxmyIcon type="close" className="text-6xl text-white" />
                </Button>
                <Button
                  onPress={handleApplyEffects}
                  isIconOnly
                  size="lg"
                  variant="light"
                  radius="lg"
                >
                  <TxmyIcon type="checkmark" className="text-6xl text-white" />
                </Button>
              </div>
            </div>
          )}
        </Drawer>
        <InfinitySlidingMenu
          isOpen={isSlidingMenuOpen}
          onOpenChange={handleSlidingMenuClose}
        />
      </div>
    </div>
  );
}
